"use client";
import { InfosupCover } from "@/app/(main)/voyage/[aliasId]/_components/InfosupCover";
import {
  setLastRequestProgram,
  setRequestProgram,
} from "@/app/(main)/voyage/[aliasId]/_components/RequestProgram/lastRequestProgram";
import { useRequestProgramFITMutation } from "@/app/api/request-program-fit/front";
import { RequestProgramFITInputSchema } from "@/app/api/request-program-fit/schemaTypes";
import { PhoneInput } from "@/components/PhoneInput";
import { PreventScroll } from "@/components/PreventScroll";
import { ArrowRight } from "@/components/icons/ArrowRight";
import { Close } from "@/components/newIcons/Close";
import { setUserInfo } from "@/lib/userInfo";
import { valibotResolver } from "@hookform/resolvers/valibot";
import clsx from "clsx";
import { useEffect, useRef, useState } from "react";
import usePortal from "react-cool-portal";
import { type SubmitHandler, useForm } from "react-hook-form";
import { Input } from "ui";
import { PrimaryButton } from "ui/components";
import { Checkbox } from "ui/inputs";
import type { InferInput, InferOutput } from "valibot";
import type { RequestProgramFITTravelImage } from ".";

type Props = {
  travelId: number;
  travelTitle: string;
  travelImage: RequestProgramFITTravelImage;
  travelCountries: Array<{ title: string; alias: string | null }>;
  programUrl: string;
  onHide: () => void;
};
export const Form = ({ travelId, travelTitle, travelImage, travelCountries, programUrl, onHide }: Props) => {
  const [transition, setTransition] = useState(false);
  const programWindowRef = useRef<Window | null>(null);
  const { Portal } = usePortal({
    defaultShow: true,
    internalShowHide: false,
  });
  const { control, handleSubmit } = useForm<InferInput<RequestProgramFITInputSchema>>({
    resolver: valibotResolver(RequestProgramFITInputSchema),
    defaultValues: {
      type: "anonymous",
      email: "",
      phone: "",
      travelId,
      openTracking: false,
    } satisfies InferInput<RequestProgramFITInputSchema>,
  });

  const openPendingProgram = () => {
    const programWindow = programWindowRef.current;
    programWindowRef.current = null;

    if (programWindow && !programWindow.closed) {
      programWindow.location.href = programUrl;
      return;
    }

    const a = document.createElement("a");
    a.href = programUrl;
    a.target = "_blank";
    a.click();
    a.remove();
  };

  const [request, { isLoading }] = useRequestProgramFITMutation({
    onCompleted: (data) => {
      if (data.success) {
        setLastRequestProgram();
        setRequestProgram();
        openPendingProgram();

        setUserInfo({
          email: data.email,
          telephone: data.telephone,
        });

        onHide();
      }
    },
  });

  useEffect(() => {
    setTimeout(() => setTransition(true), 0);
  }, []);

  const onSubmit: SubmitHandler<InferOutput<RequestProgramFITInputSchema>> = async (data) => {
    programWindowRef.current = window.open("about:blank", "_blank");

    const result = await request(data);
    if (!result.data?.success) {
      programWindowRef.current?.close();
      programWindowRef.current = null;
    }
  };

  return (
    <Portal>
      <div onClick={onHide} className="fixed inset-0 z-50 bg-primary/80 p-4 flex flex-col justify-center">
        <PreventScroll />
        <form
          className={clsx(
            "w-full max-w-xl mx-auto bg-white border border-solid border-[#E5E5E5] rounded-[5px] shadow-sm text-primary overflow-hidden grid grid-cols-1 scale-0",
            {
              "transition-transform scale-100": transition,
            },
          )}
          onClick={(e) => e.stopPropagation()}
          onSubmit={handleSubmit(onSubmit)}
        >
          <div className="h-44 sm:h-52 relative overflow-hidden">
            <div className="absolute -inset-4 bg-fit-1/30 z-10" />
            {travelImage !== null && (
              <div className="absolute -inset-4 blur-md">
                <img
                  alt="Couverture du voyage"
                  className="w-full h-full object-cover"
                  src={`https://assets.intermedes.voyage/images/travels/${travelId}/${travelImage.name}/640/${travelImage.realname}?ratio=66.66`}
                  loading="lazy"
                  decoding="async"
                />
              </div>
            )}
            <div className="relative inset-0 z-20 h-full">
              <p className="text-white font-bold text-xl sm:text-2xl pr-48 pl-4 pt-4 sm:pr-60 sm:pl-10 sm:pt-16">
                Découvrez le programme complet du voyage !
              </p>
              <button
                type="button"
                className="absolute right-4 top-4 border-2 border-[#C1B1AC] bg-white rounded-lg p-2"
                onClick={onHide}
              >
                <Close className="w-4 h-4 text-primary" />
              </button>
              <InfosupCover
                travelId={travelId}
                travelTitle={travelTitle}
                travelImage={travelImage}
                dateStart={null}
                dateEnd={null}
                countries={travelCountries}
                className="absolute right-16 sm:right-20 -bottom-2"
                type="popup"
              />
            </div>
          </div>
          <div className="flex flex-col space-y-4 p-4 lg:p-6 overflow-y-auto">
            <p className="text-lg lg:text-xl leading-6 font-semibold">{travelTitle}</p>
            <p>Confiez-nous vos coordonnées pour télécharger le programme complet.</p>
            <Input id="email" label="Email *" placeholder="Saisir votre adresse email" name="email" control={control} />
            <PhoneInput
              id="phone"
              label="Votre numéro de téléphone"
              placeholder="Saisir votre numéro de téléphone"
              name="phone"
              control={control}
            />
            <Checkbox name="openTracking" control={control}>
              J’accepte que les ouvertures de mes e-mails soient prises en compte afin d’améliorer les communications
              qui me sont adressées.
            </Checkbox>
            <div className="flex justify-center">
              <PrimaryButton type="submit" disabled={isLoading} className="w-full lg:w-auto">
                Télécharger l'itinéraire complet
                <ArrowRight className="w-4 h-4" />
              </PrimaryButton>
            </div>
            <div className="text-[11px] text-tx-3">
              En renseignant votre adresse e-mail, vous recevrez le programme détaillé demandé. Vous acceptez également
              de recevoir les actualités et offres d’Intermèdes. Afin de vous accompagner dans votre projet de voyage,
              l’un de nos conseillers pourra également vous contacter pour vous communiquer des informations
              complémentaires ou répondre à vos questions, si cela peut vous être utile. Vous pouvez à tout moment vous
              désinscrire de nos newsletters, demander à ne plus être contacté par nos conseillers et modifier vos
              préférences de confidentialité.
            </div>
          </div>
        </form>
      </div>
    </Portal>
  );
};
